import { NonNullableProps } from '@/typings/utils'

import maxBy from 'lodash/maxBy'
import omit from 'lodash/omit'
import { getRangeDatesUntil } from '@/helpers/dateFormatter'
import { SheetCellTypes, SheetCellTypeMethods } from '@/typings/api/FormTemplate'


const DATE_FORMAT = 'DD MM YYYY'

interface FormTemplate {
  id: number;
  name: string;
  description: string;
  sheets: Sheet[];
}

interface Sheet {
  name: string;
  uuid: string;
  description: string;
  cells: Cell[];
}

interface Cell {
  name: string;
  uuid: string;
  description: string;
  type: SheetCellTypes;
  type_method: SheetCellTypeMethods;

  list: string[];

  auto_start: number;

  number_from: number;
  number_to: number;

  string_min: number;
  string_max: number;

  date_from: string;
  date_to: string;

  formula: string;
}

export const getRangeListFromCell = (cell: Cell) => {
  let list = null

  switch (cell.type) {
    case SheetCellTypes.date_interval:
      list = getRangeDatesUntil(cell.date_from, cell.date_to, 'days')
        .map((date) => date.format(DATE_FORMAT))
      break

    case SheetCellTypes.number_interval: {
      const length = cell.number_to - cell.number_from + 1
      list = Array.from({ length }, (_, i) => cell.number_from + i)
      break
    }

    case SheetCellTypes.list_interval:
      list = cell.list
      break
    default:
  }

  return list
}

export const createCellData = (
  cells: Cell[],
  defaults: Record<string, unknown> = {},
) => {
  const result = cells.reduce((acc, cell, cell_uuid) => {
    const value = defaults[cell_uuid]

    if (value) acc[cell_uuid] = value

    return acc
  }, {} as Record<number | '$readonly', unknown>)

  return result
}

export const createSheetData = (cells: Cell[]) => {
  const list: Record<number | '$readonly', unknown>[] = []

  const auto_intervals = cells
    .map((cell, cell_uuid) => ({ cell, cell_uuid, list: getRangeListFromCell(cell) }))
    .filter((_) => _.list?.length)

  if (!auto_intervals.length) {
    const data = createCellData(cells)
    return [data]
  }

  const max = maxBy(auto_intervals, 'list.length') as NonNullableProps<typeof auto_intervals[0]>

  max.list.forEach((_: unknown, index: number) => {
    const defaults = auto_intervals.reduce((acc, el) => {
      acc[el.cell_uuid] = el.list?.[index]
      return acc
    }, {} as Record<string, unknown>)
    const data = createCellData(cells, defaults)
    list.push(data)
  })

  return list
}

export const isNewable = (cells: Cell[]) => {
  const auto_intervals = cells
    .map((cell, cell_uuid) => ({ cell, cell_uuid, list: getRangeListFromCell(cell) }))
    .filter((_) => _.list?.length)

  return !auto_intervals.length
}

export const downloadDocument = async (
  formTemplate: FormTemplate,
  dataCollection: ReturnType<typeof createSheetData>[],
) => {
  const [xlsx, alasql] = await Promise.all([
    await import(
      /* webpackChunkName: "xlsx" */
      'xlsx'
    ),
    await import(
      /* webpackChunkName: "alasql" */
      'alasql'
    ).then((_) => _.default),
  ])

  const result = formTemplate.sheets.reduce((accAll, sheet, index) => {
    const options = {
      sheetid: sheet.name,
      headers: false,
    }

    const header = sheet.cells.map((_) => _.name)
    const list = [header, ...dataCollection[index].map((data) => omit(data, '$readonly'))]

    accAll.options.push(options)
    accAll.data.push(list)

    return accAll
  }, { options: [], data: [] } as { options: unknown[]; data: unknown[] })

  alasql.setXLSX(xlsx)

  alasql(
    `SELECT INTO XLSX("${formTemplate.name}.xlsx",?) FROM ?`,
    [result.options, result.data],
  )
}
